import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getPeopleDetails,
  getuserDetails,
  searchUserDetails,
  downloadBulkTemplate,
  bulkUpdateWithPresignedUrl,
} from "../../store/PeopleSlice/thunk";
import { peopleActions } from "../../store/PeopleSlice";
import { generatePresignedURL } from "../../store/RequestSlice/thunk";
import { downloadBulkSeperationTemplate } from "../../store/SeperationSlice/thunk";
import { seperationActions } from "../../store/SeperationSlice";
import { PeopleDetailsSkeleton, PeopleSkeleton } from "../../utils/skeleton";
import KeyIcon from "../../assets/icons/key_black.svg";
import PersonIcon from "../../assets/icons/person_add.svg";
import SearchIcon from "../../assets/icons/search.svg";
import CloseIcon from "../../assets/icons/close.svg";
import ArrowbackIcon from "../../assets/icons/arrow_back.svg";
import EmployeeCard from "../../components/EmployeeCard";
import RecentIcon from "../../assets/icons/recent_added.svg";
import InitialsCircle from "../../components/InitialsCircle";
import DownloadIcon from "../../assets/icons/download.svg";
import DeleteIcon from "../../assets/icons/delete.svg";
import DocImg from "../../assets/images/xlsx_img.jpg";
import TemplateImg from "../../assets/icons/template.svg";
import LogoImg from "../../assets/icons/logo.svg";
import CommonModal from "../../commonComponents/CommonModal";
import Lottie from "react-lottie";
import successAnimationData from "../../assets/animations/success.json";
import "./People.css";

const People = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const peopleData = useSelector((state) => state.people.peopleDetails);
  const [searchClicked, setSearchClicked] = useState(false);
  const [isDraftSelected, setIsDraftSelected] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [allEmployees, setAllEmployees] = useState([]);
  const [showMobileActions, setShowMobileActions] = useState(false);
  const [fileUploadErr, setFileUploadErr] = useState(false);
  const [bulkFile, setBulkFile] = useState();
  const [employeeSelected, setEmployeeSelected] = useState({});
  const [isResponsivePeopleView, setIsResponsivePeopleView] = useState(
    window.innerWidth < 1280,
  );

  const searchTextRef = useRef();
  searchTextRef.current = searchText;
  const fileInputRef = useRef(null);
  const observerRef = useRef();
  const firstLoadRef = useRef(true);
  const searchResultsRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const employeeSelectTimeoutRef = useRef(null);
  const pendingSelectedEmployeeId = useRef(null);
  const openModalRef = useRef();
  const closeModalRef = useRef();
  const openSuccessModalRef = useRef();
  const closeSuccessModalRef = useRef();
  const openEmployeeDetailModalRef = useRef();
  const closeEmployeeDetailModalRef = useRef();
  const bulkFileRef = useRef();
  const employeeCardRefs = useRef({});

  const LIMIT = 18;
  const employeeID = useSelector((state) => state.auth.employeeID);
  const isEdit = useSelector((state) => state.people.isEdit);
  const isDraft = useSelector((state) => state.people.isDraft);
  const peopleDetailsStatus = useSelector(
    (state) => state.people.peopleDetailsStatus,
  );
  const user_type = useSelector(
    (state) => state.auth?.loginResponse?.body?.role_type,
  );
  const searchList = useSelector((state) => state.people.searchList);
  const searchListStatus = useSelector(
    (state) => state.people.searchListStatus,
  );
  const userDetails =
    useSelector((state) => state.people.userDetails?.user_details) || {};
  const userDetailsStatus = useSelector(
    (state) => state?.people?.userDetailsStatus,
  );
  const downloadBulkSeperationResponse = useSelector(
    (state) => state.seperation.downloadBulkSeperationResponse || "",
  );
  const downloadBulkSeperationResponseStatus = useSelector(
    (state) => state.seperation.downloadBulkSeperationResponseStatus || "",
  );
  const bulkUpdateUsersResponse = useSelector(
    (state) => state.people.bulkUpdateUsersResponse || "",
  );
  const bulkUpdateUsersResponseStatus = useSelector(
    (state) => state.people.bulkUpdateUsersResponseStatus || "",
  );

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: successAnimationData,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  useEffect(() => {
    dispatch(peopleActions.resetUserDetails());
    dispatch(peopleActions.resetSearchList());
    setEmployeeSelected({});
    setSearchClicked(false);

    return () => {
      if (employeeSelectTimeoutRef.current) {
        clearTimeout(employeeSelectTimeoutRef.current);
      }
      dispatch(peopleActions.resetUserDetailsData());
      dispatch(peopleActions.resetSearchList());
      dispatch(peopleActions.resetUserDetails());
      dispatch(peopleActions.resetPeopleDetails());
      dispatch(peopleActions.resetBulkUpdateUserResponse());
      dispatch(seperationActions.resetDownloadBulkSeperationResponse());
    };
  }, [dispatch]);

  useEffect(() => {
    const handleResize = () => {
      setIsResponsivePeopleView(window.innerWidth < 1280);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Click outside search results to close
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        searchResultsRef.current &&
        !searchResultsRef.current.contains(event.target)
      ) {
        setShowSearchResults(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Initially load all employees
  useEffect(() => {
    dispatch(peopleActions.resetPeopleDetails());
    setAllEmployees([]);
    dispatch(
      getPeopleDetails({ offset: 0, limit: LIMIT, draft: isDraftSelected }),
    );
  }, [dispatch, isDraftSelected]);

  // Handle people data updates
  useEffect(() => {
    if (peopleDetailsStatus === "SUCCESS" && peopleData?.user_details) {
      if (peopleData.user_details.length > 0) {
        if (offset === 0) {
          // First page
          setAllEmployees(peopleData.user_details);
          firstLoadRef.current = false;
        } else {
          // Append new employees using functional update to avoid dependency on allEmployees
          setAllEmployees((prev) => {
            const existingIds = new Set(prev.map((emp) => emp.employee_id));
            const newEmployees = peopleData.user_details.filter(
              (emp) => !existingIds.has(emp.employee_id),
            );
            return [...prev, ...newEmployees];
          });
        }
        setHasMore(peopleData.user_details.length >= LIMIT);
      } else {
        if (offset === 0) {
          setAllEmployees([]);
        }
        setHasMore(false);
      }
    }
  }, [peopleData, peopleDetailsStatus, offset]);

  // Load more data on scrolling
  const loadMoreEmployees = useCallback(() => {
    if (peopleDetailsStatus === "PENDING" || !hasMore) return;

    const newOffset = offset + LIMIT;
    setOffset(newOffset);
    dispatch(
      getPeopleDetails({
        offset: newOffset,
        limit: LIMIT,
        draft: isDraftSelected,
      }),
    );
  }, [dispatch, offset, peopleDetailsStatus, hasMore, isDraftSelected]);

  // Set up intersection observer for infinite scrolling
  const lastEmployeeElementRef = useCallback(
    (node) => {
      if (peopleDetailsStatus === "PENDING") return;

      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            loadMoreEmployees();
          }
        },
        { threshold: 0.1 },
      );

      if (node) {
        observerRef.current.observe(node);
      }
    },
    [peopleDetailsStatus, hasMore, loadMoreEmployees],
  );

  useEffect(() => {
    const selectedEmployeeId =
      employeeSelected?.employee_id || userDetails?.employee_id;
    const selectedCard = selectedEmployeeId
      ? employeeCardRefs.current[selectedEmployeeId]
      : null;

    if (selectedCard && selectedCard.scrollIntoView) {
      selectedCard.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [employeeSelected?.employee_id, userDetails?.employee_id]);

  const getSelectedUserDetails = (employee_id) => {
    if (employeeSelectTimeoutRef.current) {
      clearTimeout(employeeSelectTimeoutRef.current);
    }
    pendingSelectedEmployeeId.current = employee_id;
    employeeSelectTimeoutRef.current = setTimeout(() => {
      dispatch(
        getuserDetails({
          employee_id: pendingSelectedEmployeeId.current,
          user_type: user_type,
          draft: isDraftSelected,
        }),
      );
      employeeSelectTimeoutRef.current = null;
      pendingSelectedEmployeeId.current = null;
    }, 300);
  };

  const draftButtonHandler = () => {
    setSearchText("");
    setSearchClicked(false);
    setIsDraftSelected(!isDraftSelected);
    dispatch(peopleActions.resetSearchList());
    dispatch(peopleActions.resetUserDetails());
    dispatch(peopleActions.updateDraft(false));
    // setOffset(0)
    // dispatch(getPeopleDetails({ offset: 0, limit: LIMIT, draft: !isDraftSelected }));
  };

  const onSearchUser = (searchText) => {
    if (!searchText.trim()) {
      setShowSearchResults(false);
      return;
    }

    dispatch(
      searchUserDetails({
        id_or_name: searchTextRef.current,
        draft: isDraftSelected,
      }),
    ).then(() => {
      setShowSearchResults(true);
    });
  };

  const handleSearchChange = (e) => {
    const inputValue = e.target.value;
    setSearchText(inputValue);

    if (inputValue.length < 3) {
      setShowSearchResults(false);
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      return;
    }
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      onSearchUser(inputValue);
    }, 500);
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];

    if (file) {
      console.log("ffile selected:", file.name, file.type, file.size);
      if (
        !file.type.match(/application\/vnd\.ms-excel/) &&
        !file.type.match(
          /application\/vnd\.openxmlformats-officedocument\.spreadsheetml\.sheet/,
        )
      ) {
        if (fileInputRef.current) {
          fileInputRef.current.value = null;
        }
        return;
      }

      try {
        let payload = {
          key: "bulk_upload",
          employee_id: employeeID,
          file_name: file?.name,
        };
        dispatch(generatePresignedURL({ payload: payload, file: file }));
      } catch (error) {
        console.error("Upload failed:", error);
      } finally {
        if (fileInputRef.current) {
          fileInputRef.current.value = null;
        }
      }
    }
  };

  const onUploadBulkButtonClicked = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      dispatch(peopleActions.resetUserDetailsStatus());
    };
  }, [dispatch]);

  useEffect(() => {
    if (downloadBulkSeperationResponseStatus === "SUCCESS") {
      window.open(downloadBulkSeperationResponse?.body);
      dispatch(seperationActions.resetDownloadBulkSeperationResponse());
    } else if (downloadBulkSeperationResponseStatus === "ERROR") {
      closeModalRef.current.click();
    }
  }, [downloadBulkSeperationResponseStatus, downloadBulkSeperationResponse?.body, dispatch]);

  useEffect(() => {
    setBulkFile();
    if (bulkUpdateUsersResponseStatus === "SUCCESS") {
      closeModalRef.current.click();
      openSuccessModalRef.current.click();
      bulkFileRef.current.value = "";
    } else if (bulkUpdateUsersResponseStatus === "ERROR") {
      bulkFileRef.current.value = "";
      closeModalRef.current.click();
    }
  }, [bulkUpdateUsersResponseStatus]);

  useEffect(() => {
    if (isEdit && userDetailsStatus === "SUCCESS") {
      navigate("../people/add");
    }
  }, [isEdit, userDetailsStatus, navigate]);

  useEffect(() => {
    if (isDraft && userDetailsStatus === "SUCCESS") {
      navigate("../people/add");
    }
  }, [isDraft, userDetailsStatus, navigate]);

  const onUpdateUserInfo = () => {
    if (isDraftSelected) {
      onUpdateDraftInfo();
    } else {
      dispatch(peopleActions.updateEdit(true));
    }
  };

  const onUpdateDraftInfo = () => {
    dispatch(peopleActions.updateDraft(true));
  };

  const onDownloadTemplate = () => {
    dispatch(
      downloadBulkSeperationTemplate({
        key: "jedi-bucket/bulk_update_user_details_template.xlsx",
      }),
    );
  };

  const isXlsxFile = (file) => {
    if (!file) return false;
    const name = file.name || "";
    const extOk = /\.xlsx?$/i.test(name);
    const mimeOk =
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "application/vnd.ms-excel";
    return extOk || mimeOk;
  };

  const onReadBulkFile = (file) => {
    setFileUploadErr(false);
    if (!isXlsxFile(file)) {
      setFileUploadErr(true);
      if (bulkFileRef.current) bulkFileRef.current.value = null;
      return;
    } else {
      setBulkFile(file);
    }
  };

  const onUpdateBulkEmployees = () => {
    let payload = {
      key: "bulk_update_user_details",
      employee_id: employeeID,
      file_name: bulkFile?.name,
    };
    dispatch(bulkUpdateWithPresignedUrl({ payload: payload, file: bulkFile }));
  };

  const onSelectEmployeeCard = (item) => {
    getSelectedUserDetails(item.employee_id);
    if (isDraftSelected) {
      onUpdateDraftInfo();
    }
    setEmployeeSelected(item);

    if (isResponsivePeopleView) {
      openEmployeeDetailModalRef.current?.click();
    }
  };

  const onCloseResponsiveEmployeePopup = () => {
    if (employeeSelectTimeoutRef.current) {
      clearTimeout(employeeSelectTimeoutRef.current);
      employeeSelectTimeoutRef.current = null;
    }
    pendingSelectedEmployeeId.current = null;
    closeEmployeeDetailModalRef.current?.click();
    setEmployeeSelected({});
    dispatch(peopleActions.resetUserDetails());
  };

  const showDesktopExpansion =
    !isResponsivePeopleView &&
    (userDetails?.employee_id || userDetailsStatus === "PENDING");

  const shouldShowNoDataInResponsiveModal =
    !!employeeSelected?.employee_id &&
    userDetailsStatus !== "PENDING" &&
    (userDetailsStatus === "ERROR" ||
      (userDetailsStatus === "SUCCESS" && !userDetails?.employee_id));

  const shouldShowResponsiveLoading =
    !!employeeSelected?.employee_id && userDetailsStatus === "PENDING";

  const onOnboardingRequestsClick = () => {
    navigate("../people/review-queue");
  };

  const UpdateBulkUpload = () => (
    <div className="flex flex-col">
      <div className="cfr-subtext">
        Upload file with updated fileds to edit details individually or in bulk.
      </div>

      {bulkFile ? (
        <>
          <div className="dc-main-box mt-2 postion-relative">
            <div className="flex justify-between w-full p-12 mb-auto">
              <div className="flex items-center ml-auto">
                <div className="dc-card-ico-wrap">
                  <img src={DownloadIcon} className="dc-card-ico" alt="" />
                </div>
                <div className="dc-card-ico-wrap">
                  <img
                    src={DeleteIcon}
                    className="dc-card-ico"
                    alt="delete"
                    onClick={() => {
                      setBulkFile();
                      setFileUploadErr(false);
                    }}
                  />
                </div>
              </div>
            </div>
            <img className="dc-file-preview" src={DocImg} alt="doc" />
            <div className="flex dc-card-nameplate mt-auto w-full p-12">
              <div className="mr-auto dc-card-filename">{bulkFile?.name}</div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="temp-ico-box flex justify-between">
            <div className="flex items-center">
              <div className="temp-ico-wrap">
                <img
                  alt="template-ico"
                  className="temp-ico"
                  src={TemplateImg}
                />
              </div>
              <div className="temp-ico-text">template</div>
            </div>
            <div
              className="temp-ico-btn-wrap"
              onClick={() => {
                onDownloadTemplate();
              }}
            >
              <img className="cfr-btn-img" src={DownloadIcon} alt="" />
            </div>
          </div>
          <div className="flex cfr-upload">
            <div className="cfr-upload-text">
              Drag and drop here or browse to upload
            </div>
            <input
              type="file"
              className="cfr-upload-input"
              ref={bulkFileRef}
              onChange={(e) => {
                onReadBulkFile(e.target.files[0]);
              }}
            />
          </div>
        </>
      )}
      {fileUploadErr && (
        <div className="valid-text">
          Please upload a valid file format(.xlsx)
        </div>
      )}
      <button
        className="btn p-btn p-btn-og justify-center mt-3"
        onClick={() => onUpdateBulkEmployees()}
        disabled={!bulkFile || fileUploadErr}
      >
        <div className="p-btn-text">Update Employee Details</div>
      </button>
    </div>
  );

  const CreateFromTemplateSuccessModal = () => (
    <div className="flex flex-col">
      <div className="flex justify-center">
        <div className="animation-container">
          <Lottie options={defaultOptions} height={300} width={300} />
        </div>
      </div>
      <div className="cfts-text">Employees Profile Updated Successfully.</div>
      {bulkUpdateUsersResponse?.download_url && (
        <div className="temp-ico-box flex justify-between">
          <div className="flex items-center">
            <div className="temp-ico-wrap">
              <img alt="template-ico" className="temp-ico" src={TemplateImg} />
            </div>
            <div className="temp-ico-text">Download Updated File</div>
          </div>
          <div
            className="temp-ico-btn-wrap"
            onClick={() => {
              window.open(bulkUpdateUsersResponse?.download_url);
            }}
          >
            <img className="cfr-btn-img" src={DownloadIcon} alt="" />
          </div>
        </div>
      )}
      <button
        className="btn p-btn p-btn-og justify-center"
        onClick={() => {
          closeSuccessModalRef.current.click();
          dispatch(peopleActions.resetBulkUpdateUserResponse());
        }}
      >
        <div className="p-btn-text">Done</div>
      </button>
    </div>
  );

  return (
    <div className="flex people-wrapper">
      <div
        className={`flex flex-col ${
          userDetails?.employee_id ? "people-sm" : "people"
        }`}
      >
        <div className="flex items-center">
          <button
            type="button"
            aria-label="Back to all modules"
            className="xl:hidden mr-3 flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-slate-100 border-0"
            onClick={() => navigate("../all")}
          >
            <img src={ArrowbackIcon} alt="Back" className="h-5 w-5" />
          </button>
          <div className="p-head !pl-0 font-semibold">people</div>
          {user_type === "HR" && (
            <>
              <div className="hidden lg:flex w-full">
                <div
                  className="p-btn p-btn-manage"
                  onClick={() => navigate("../people/manage")}
                >
                  <img className="p-btn-icon" alt="" src={KeyIcon} />
                  <div className="p-manage-text">Manage Access</div>
                </div>
                <div className="dropdown position-relative ml-8">
                  <button
                    className="btn p-btn p-btn-drop dropdown-toggle btn-bulk"
                    type="button"
                    id="bulkDropdown"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <div className="p-btn-text">Bulk Actions</div>
                  </button>
                  <ul className="dropdown-menu custom-dropdown-menu">
                    <li
                      className="dropdown-option"
                      onClick={onUploadBulkButtonClicked}
                    >
                      <div className="p-btn-text-black">Add Bulk Employees</div>
                    </li>
                    <li
                      className="dropdown-option"
                      onClick={() => dispatch(downloadBulkTemplate({}))}
                    >
                      <div className="p-btn-text-black">
                        Add Bulk Template Download
                      </div>
                    </li>
                    <li
                      className="dropdown-option"
                      onClick={() => openModalRef.current.click()}
                    >
                      <div className="p-btn-text-black">
                        Update Bulk Employees
                      </div>
                    </li>
                  </ul>

                  {/* Hidden file input */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                    accept=".xls, .xlsx, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  />
                </div>
                <button
                  className="btn p-btn p-btn-og"
                  onClick={() => navigate("../people/add")}
                >
                  <img className="p-btn-icon" alt="" src={PersonIcon} />
                  <div className="p-btn-text">Add New Employee</div>
                </button>
                <button
                  className="btn p-btn p-btn-og"
                  onClick={onOnboardingRequestsClick}
                >
                  <span
                  className="material-symbols-rounded"
                  style={{ fontSize: "16px", color: "white" }}
                  aria-hidden="true"
                >
                  how_to_reg
                </span>
                  <div className="p-btn-text">Onboarding Requests</div>
                </button>
              </div>

              <button
                type="button"
                aria-label="Open actions"
                className="ml-auto lg:hidden p-2 rounded-md bg-white border-0"
                onClick={() => setShowMobileActions((s) => !s)}
              >
                <span
                  class="material-symbols-outlined"
                  style={{ fontSize: "20px" }}
                >
                  more_vert
                </span>
              </button>
            </>
          )}
        </div>

        {user_type === "HR" && showMobileActions && (
          <div className="mobile-actions-overlay lg:hidden panel">
            <div
              // className="mobile-actions-backdrop"
              onClick={() => setShowMobileActions(false)}
            />

            <div className="mobile-actions-panel">
              <div className="flex justify-between align-center p-2">
                <img className="panel-img" src={LogoImg} alt="" />
                <button
                  aria-label="Close actions"
                  className="mobile-actions-close"
                  onClick={() => setShowMobileActions(false)}
                >
                  ×
                </button>
              </div>

              <div className="flex flex-col pb-2 pl-2 pr-2">
                <div
                  className="mobile-action-item flex items-center justify-start"
                  onClick={() => {
                    navigate("../people/manage");
                    setShowMobileActions(false);
                  }}
                >
                  <span
                    class="material-symbols-outlined mr-2"
                    style={{ fontSize: "16px", color: "black" }}
                  >
                    key
                  </span>
                  <div className="mobile-action-text">Manage Access</div>
                </div>
                <div
                  className="mobile-action-item flex items-center justify-start"
                  onClick={() => {
                    onUploadBulkButtonClicked();
                    setShowMobileActions(false);
                  }}
                >
                  <span
                    class="material-symbols-outlined mr-2"
                    style={{ fontSize: "16px", color: "black" }}
                  >
                    add
                  </span>
                  <div className="mobile-action-text">Add Bulk Employees</div>
                </div>
                <div
                  className="mobile-action-item flex items-center justify-start"
                  onClick={() => {
                    dispatch(downloadBulkTemplate({}));
                    setShowMobileActions(false);
                  }}
                >
                  <span
                    class="material-symbols-outlined mr-2"
                    style={{ fontSize: "16px", color: "black" }}
                  >
                    download
                  </span>
                  <div className="mobile-action-text">
                    Add Bulk Template Download
                  </div>
                </div>
                <div
                  className="mobile-action-item flex items-center justify-start"
                  onClick={() => {
                    openModalRef.current.click();
                    setShowMobileActions(false);
                  }}
                >
                  <span
                    class="material-symbols-outlined mr-2"
                    style={{ fontSize: "16px", color: "black" }}
                  >
                    update
                  </span>
                  <div className="mobile-action-text">
                    Update Bulk Employees
                  </div>
                </div>
                {/* <div
                  className="mobile-action-item flex items-center justify-start"
                  onClick={() => {
                    onOnboardingRequestsClick();
                    setShowMobileActions(false);
                  }}
                >
                  <span
                    class="material-symbols-outlined mr-2"
                    style={{ fontSize: "16px", color: "black" }}
                  >
                    assignment
                  </span>
                  <div className="mobile-action-text">Onboarding Requests</div>
                </div> */}
              </div>
            </div>
          </div>
        )}
        <div className="flex p-filter">
          {searchClicked && userDetails?.employee_id ? (
            <div className="p-searched-tag">
              <img src={SearchIcon} className="p-search-icon" alt="Search" />
              <div className="search-tag-name">
                {userDetails?.full_name?.value || userDetails?.first_name}
              </div>
              {searchText && (
                <img
                  src={CloseIcon}
                  className="p-close-icon"
                  alt="Search"
                  onClick={() => {
                    setShowSearchResults(false);
                    setSearchText("");
                    setSearchClicked(false);
                    dispatch(peopleActions.resetSearchList());
                  }}
                />
              )}
            </div>
          ) : (
            <div className="position-relative">
              <div className="p-search-container">
                <img src={SearchIcon} className="p-search-icon" alt="Search" />
                <input
                  type="text"
                  className="p-search"
                  value={searchText}
                  onChange={handleSearchChange}
                  onSelect={() => {
                    if (searchText.length > 3) setShowSearchResults(true);
                  }}
                  placeholder="Search by employee name & id"
                />
                {searchText && (
                  <img
                    src={CloseIcon}
                    className="ps-close-icon"
                    alt="Search"
                    onClick={() => {
                      setShowSearchResults(false);
                      setSearchText("");
                      setSearchClicked(false);
                      dispatch(peopleActions.resetSearchList());
                    }}
                  />
                )}
              </div>

              {showSearchResults && (
                <div
                  className="search-results-dropdown"
                  ref={searchResultsRef}
                  onClick={(e) => e.stopPropagation()}
                >
                  {searchListStatus === "PENDING" ? (
                    <div className="search-no-results">Searching...</div>
                  ) : searchList?.user_details &&
                    searchList.user_details.length > 0 ? (
                    searchList.user_details.map((item) => (
                      <div
                        key={item.employee_id}
                        className={`search-result-item ${
                          userDetails.employee_id?.value === item.employee_id
                            ? "selected"
                            : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSearchClicked(true);
                          setShowSearchResults(false);
                          onSelectEmployeeCard({ employee_id: item.employee_id });
                        }}
                      >
                        <div className="search-result-details">
                          <InitialsCircle
                            fullName={item?.full_name || item?.first_name}
                            size={24}
                          />
                          {/* <img
                            className="search-emp-icon"
                            src={UserIcon}
                            alt="emp-icon"
                          /> */}
                          <span className="search-result-name">
                            {item?.full_name || item?.first_name}
                          </span>
                          <span className="dot"> • </span>
                          <span className="search-result-id">
                            ID #{item.employee_id}
                          </span>
                          <span className="dot"> • </span>
                          <span className="search-result-desgn">
                            {item.designation}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="search-no-results">No employees found</div>
                  )}
                </div>
              )}
            </div>
          )}
          {user_type === "HR" && window.innerWidth >= 768 && (
            <div className="p-sort-bar"></div>
          )}
          {user_type === "HR" && window.innerWidth >= 768 && (
            <div
              className={`flex rc-sort-wrap ${
                isDraftSelected ? "rc-sort-wrap-selected" : ""
              } mr8`}
              onClick={draftButtonHandler}
            >
              <img src={RecentIcon} className="filter-icon" alt="" />
              <div className="rc-filter-text">Draft</div>
            </div>
          )}
        </div>
        <div
          className={
            userDetails?.employee_id && !isResponsivePeopleView
              ? "employee-cards-expanded"
              : "employee-cards"
          }
        >
          {searchClicked && userDetails?.employee_id ? (
            <div
              ref={(node) => {
                if (userDetails?.employee_id) {
                  if (node) {
                    employeeCardRefs.current[userDetails.employee_id] = node;
                  } else {
                    delete employeeCardRefs.current[userDetails.employee_id];
                  }
                }
              }}
            >
              <EmployeeCard
                isExpanded={false}
                data={userDetails}
                onSelectEmployee={() => {}}
                employeeSelected={employeeSelected}
              />
            </div>
          ) : (
            allEmployees?.map((item, index) => {
              const isLastElement = allEmployees.length === index + 1;

              return (
                <div
                  key={`${item.employee_id}-${index}`}
                  ref={(node) => {
                    if (isLastElement) {
                      lastEmployeeElementRef(node);
                    }
                    if (node) {
                      employeeCardRefs.current[item.employee_id] = node;
                    } else {
                      delete employeeCardRefs.current[item.employee_id];
                    }
                  }}
                >
                  <EmployeeCard
                    isExpanded={false}
                    data={item}
                    onSelectEmployee={() => onSelectEmployeeCard(item)}
                    userDetails={item}
                    employeeSelected={employeeSelected}
                  />
                </div>
              );
            })
          )}

          {/* {!hasMore && allEmployees.length > 0 && (
            <div className="end-of-results">No more employees to load</div>
          )} */}

          {peopleDetailsStatus !== "PENDING" && allEmployees.length === 0 && (
            <div className="no-results">No employees found</div>
          )}
        </div>
        {peopleDetailsStatus === "PENDING" && <PeopleSkeleton />}
      </div>

      {showDesktopExpansion &&
        (userDetailsStatus === "PENDING" ? (
          <div
            style={{ width: "617px", height: "97vh", padding: "12px" }}
            // className="employee-card-placeholder p-24 flex flex-col items-center justify-center"
          >
            <PeopleDetailsSkeleton />
          </div>
        ) : userDetails?.employee_id ? (
          <EmployeeCard
            isExpanded={true}
            data={userDetails}
            onSelectEmployee={() => {
              setEmployeeSelected({});
              dispatch(peopleActions.resetUserDetails());
            }}
            onUpdateInfo={onUpdateUserInfo}
            employeeSelected={employeeSelected}
          />
        ) : null)}

      {isResponsivePeopleView && (
        <CommonModal
          openRef={openEmployeeDetailModalRef}
          closeRef={closeEmployeeDetailModalRef}
          id={"peopleEmployeeDetailsModal"}
          hideCloseButton={true}
          outSideCloseButton={false}
          backDrop={false}
          disabled={false}
          size={"lg"}
          modalStyle={{
            maxWidth: "760px",
            width: "100%",
            backgroundColor: "transparent",
            border: "none",
            borderRadius: "24px",
          }}
          modalBodyStyle={{
            padding: "0px",
          }}
          onModalClose={() => {
            if (employeeSelectTimeoutRef.current) {
              clearTimeout(employeeSelectTimeoutRef.current);
              employeeSelectTimeoutRef.current = null;
            }
            pendingSelectedEmployeeId.current = null;
            setEmployeeSelected({});
            dispatch(peopleActions.resetUserDetails());
          }}
          content={
            shouldShowResponsiveLoading ? (
              <div className="people-mobile-detail-loading">
                <PeopleDetailsSkeleton />
              </div>
            ) : userDetails?.employee_id ? (
              <EmployeeCard
                isExpanded={true}
                data={userDetails}
                onSelectEmployee={onCloseResponsiveEmployeePopup}
                onUpdateInfo={onUpdateUserInfo}
                employeeSelected={employeeSelected}
              />
            ) : shouldShowNoDataInResponsiveModal ? (
              <div className="people-mobile-detail-empty">No data available</div>
            ) : (
              <div className="people-mobile-detail-empty">No data available</div>
            )
          }
        />
      )}

      <CommonModal
        openRef={openModalRef}
        closeRef={closeModalRef}
        id={"updateBulkUploadModal"}
        hideCloseButton={false}
        content={<UpdateBulkUpload />}
        disabled={false}
        size={"md"}
        modalHeadText={"Update Bulk Employees"}
      />

      <CommonModal
        openRef={openSuccessModalRef}
        closeRef={closeSuccessModalRef}
        id={"CreateFromTemplateSuccessModal"}
        modalHeadText={"add from template"}
        hideCloseButton={false}
        content={<CreateFromTemplateSuccessModal />}
        disabled={false}
        size={"md"}
        onModalClose={() => {
          setBulkFile();
          setFileUploadErr(false);
          if (bulkFileRef.current) {
            bulkFileRef.current.value = "";
          }
        }}
      />
    </div>
  );
};

export default People;
